import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldData } from 'create/bulk/common/types';
import { xDataKey, DropdownState } from './types';
import { useFormContext } from 'react-hook-form';
import { Button, Dropdown } from '@tcc/crate';
import Chart from './Chart';
import { LineChartProvider, SliderChart, InputChart } from '@tcc/fds-charts';
import { useParseRecommendationRows } from '../useParseRecommendationRows';
import { getMessage } from '@tcc/i18n';
import classNames from 'classnames';
import { findFieldIndexByHybridId, getRecommendationByHybridId } from '../utils';
import { useHybrids } from './hooks/useHybrids';
import { useDatasets } from './hooks/useDatasets';

interface RecommendationComparatorProps {
  field: FieldData & { _id: string; _index: number };
  onClose: () => void;
  fieldIndex: number;
}

export const RecommendationComparator = ({
  field,
  onClose,
  fieldIndex,
}: RecommendationComparatorProps) => {
  const firstRender = useRef(true);
  const { getValues, setValue } = useFormContext();
  // Get all the fields
  const fields = getValues('fields');
  // Hybrid dropdown states
  const [hybrid1, setHybrid1] = useState<DropdownState | null>({
    name: fields[fieldIndex]?.hybrid?.name || null,
    id: fields[fieldIndex]?.hybrid?.id || null,
  });
  const [hybrid2, setHybrid2] = useState<DropdownState | null>(null);
  // Get the FVSS and User's entry for the selected hybrids
  const { fvssRow: fvssRow1, yourEntryRow: yourEntryRow1 }
    = useParseRecommendationRows(getRecommendationByHybridId({ fields, fieldId: field.id, hybridId: hybrid1?.id }));

  const { fvssRow: fvssRow2, yourEntryRow: yourEntryRow2 }
    = useParseRecommendationRows(getRecommendationByHybridId({ fields, fieldId: field.id, hybridId: hybrid2?.id }));

  // Local state to manage population changes on chart changes
  const [population1, setPopulation1] = useState<number | null>(yourEntryRow1?.avgFixedPop || null);
  const [population2, setPopulation2] = useState<number | null>(yourEntryRow2?.avgFixedPop || null);

  useEffect(() => {
    // Avoiding running this effect on the first render for hybrid1
    if(firstRender.current) {
      firstRender.current = false;
      return;
    }

    // When the user selects/changes hybrid2, update population2
    setPopulation2(yourEntryRow2?.avgFixedPop || null);
  }, [yourEntryRow2]);
  const { isUniqueHybrid, hybridsList } = useHybrids({fields, fieldId: field.id, hybrid1, hybrid2});

  const handleDropdownChange = useCallback(
    (selectedHybrid: DropdownState, dropdownNumber: number) => {
      if (dropdownNumber === 1) {
        setHybrid1(selectedHybrid);
      } else {
        setHybrid2(selectedHybrid);
      }
    },
    [],
  );

  // Determines the fixed dots (A.k.a the recommendation points) based on the selected hybrids
  const fixedDots = useMemo(() => {
    const dots = [];
    if(fvssRow1?.avgFixedPop != null && fvssRow1?.estimatedYield != null) {
      dots.push({ x: fvssRow1.avgFixedPop, y: fvssRow1.estimatedYield });
    }

    if(hybrid2 && fvssRow2?.avgFixedPop != null && fvssRow2?.estimatedYield != null) {
      dots.push({ x: fvssRow2.avgFixedPop, y: fvssRow2.estimatedYield });
    }

    return dots;
  }, [fvssRow1, fvssRow2, hybrid2]);

  // Determines the dynamic dots (A.k.a the user's entry points) based on the selected hybrids
  const initialDynamicDots = useMemo(() => {
    const dots = [];
    if(yourEntryRow1?.avgFixedPop != null && yourEntryRow1?.estimatedYield != null) {
      dots.push({
        x: yourEntryRow1.avgFixedPop,
        y: yourEntryRow1.estimatedYield,
      });
    }
    if(hybrid2 && yourEntryRow2?.avgFixedPop != null && yourEntryRow2?.estimatedYield != null) {
      dots.push({
        x: yourEntryRow2.avgFixedPop,
        y: yourEntryRow2.estimatedYield,
      });
    }
    return dots;
  }, [hybrid2, yourEntryRow1, yourEntryRow2]);

  // Determines the datasets for the chart based on the selected hybrids
  const datasets = useDatasets({
    fields,
    hybrid1,
    hybrid2,
    fieldId: field.id,
  });

  // Handles the population changes
  const handlePopulationChange = useCallback((newPopulation: number, dotIndex: number) => {
    if(dotIndex === 0) setPopulation1(newPopulation);
    if(dotIndex === 1) setPopulation2(newPopulation);
  }, []);

  // Saves the draft (overrides what the user has entered previously)
  const handleSaveDraft = useCallback(() => {
    const hybrid1Field = findFieldIndexByHybridId({fields, fieldId: field.id, hybridId: hybrid1?.id});
    setValue(`fields.${hybrid1Field}.avgFixedPopulation`, population1);

    if(hybrid2) {
      const hybrid2Field = findFieldIndexByHybridId({fields, fieldId: field.id, hybridId: hybrid2.id});
      setValue(`fields.${hybrid2Field}.avgFixedPopulation`, population2);
    }
    onClose();
  }, [field.id, fields, hybrid1?.id, hybrid2, onClose, population1, population2, setValue]);

  const leftColumnClassName = classNames('left-column', {
    'one-hybrid': isUniqueHybrid,
  });

  return (
    <>
      <header>
        <div>
          <h2>{field.name}</h2>
          <span>{getMessage('text__field-level-seed-recs')}</span>
        </div>
        <Button onClick={onClose}>{getMessage('button-text__close')}</Button>
        <Button mode="primary" onClick={handleSaveDraft}>
          {getMessage('button-text__save')}
        </Button>
      </header>
      <LineChartProvider
        datasets={datasets}
        xDataKey={xDataKey.PLANTING_RATE}
        fixedDots={fixedDots}
        onUserChange={handlePopulationChange}
        initialDynamicDots={initialDynamicDots}
      >
        <section className="chart-section">
          <Chart multipleHybrids />
        </section>
        <section className="details-section">
          <div className={leftColumnClassName}>
            <Dropdown
              label={getMessage('bulk-fvss-review-scripts-hybrid-placeholder')}
              options={hybridsList}
              value={hybrid1?.name ?? undefined}
              setValue={(val: string | null, id: string | null) =>
                handleDropdownChange({ name: val, id }, 1)
              }
              width="100%"
              disabled={isUniqueHybrid}
            />
            <SliderChart dotIndex={0} />
            <InputChart label={getMessage('rate')} showLabel dotIndex={0} />

          </div>
          {!isUniqueHybrid && (
            <div className="right-column">
              <Dropdown
                label={getMessage('bulk-fvss-review-scripts-hybrid-placeholder')}
                options={hybridsList}
                value={hybrid2?.name ?? undefined}
                setValue={(val: string | null, id: string | null) =>
                  handleDropdownChange({ name: val, id }, 2)
                }
                width="100%"
              />

              <SliderChart dotIndex={1} disabled={!hybrid2} />
              <InputChart label={getMessage('rate')} showLabel dotIndex={1} disabled={!hybrid2} />
            </div>
          )}
        </section>
      </LineChartProvider>
    </>
  );
};
